<template>
  <v-card class="pa-4" flat>
    <div v-if="loading" class="d-flex justify-center align-center py-12">
      <atom-spinner
        :animation-duration="1500"
        :size="100"
        class="mx-auto"
        color="indigo"
      />
    </div>

    <v-form v-show="!loading" ref="form" v-model="valid" lazy-validation>
      <div class="pa-5 border rounded-xl" style="background: #f9f9f9">

        <!-- فیلدهای اصلی محصول -->
        <v-row>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="formData.title"
              label="عنوان محصول"
              outlined
              dense
              prepend-inner-icon="mdi-format-title"
              :rules="[rules.required]"
              :error-messages="serverErrors.title"
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="formData.sku"
              label="کد کالا (SKU مادر)"
              outlined
              dense
              prepend-inner-icon="mdi-barcode"
              :rules="[rules.required]"
              :error-messages="serverErrors.sku"
            />
          </v-col>

          <!-- ابعاد و وزن کالا -->
          <v-col cols="12" sm="3">
            <v-text-field
              v-model.number="formData.weight"
              type="number"
              label="وزن"
              suffix="gr"
              prepend-inner-icon="mdi-weight-gram"
              clearable
              outlined
              dense
              style="border-radius: 8px"
              clear-icon="mdi-close-circle-outline"
            />
          </v-col>

          <v-col cols="12" sm="3">
            <v-text-field
              v-model.number="formData.length"
              type="number"
              label="طول"
              suffix="cm"
              prepend-inner-icon="mdi-ruler"
              clearable
              outlined
              dense
              style="border-radius: 8px"
              clear-icon="mdi-close-circle-outline"
            />
          </v-col>

          <v-col cols="12" sm="3">
            <v-text-field
              v-model.number="formData.width"
              type="number"
              label="عرض"
              suffix="cm"
              prepend-inner-icon="mdi-ruler-square"
              clearable
              outlined
              dense
              style="border-radius: 8px"
              clear-icon="mdi-close-circle-outline"
            />
          </v-col>

          <v-col cols="12" sm="3">
            <v-text-field
              v-model.number="formData.height"
              type="number"
              label="ارتفاع"
              suffix="cm"
              prepend-inner-icon="mdi-arrow-up-down"
              clearable
              outlined
              dense
              style="border-radius: 8px"
              clear-icon="mdi-close-circle-outline"
            />
          </v-col>

          <!-- حداقل و حداکثر قیمت پیش‌فرض محصول -->
          <v-col cols="12" md="6">
            <price-input
              v-model="formData.default_min_price"
              label="حداقل قیمت پیش‌فرض"
              dense
              :class="{ 'has-price-error': hasPriceError }"
            />
          </v-col>

          <v-col cols="12" md="6">
            <price-input
              v-model="formData.default_max_price"
              label="حداکثر قیمت پیش‌فرض"
              dense
              :class="{ 'has-price-error': hasPriceError }"
            />
          </v-col>

          <!-- پیام خطای حداقل/حداکثر قیمت اصلی -->
          <v-col v-if="hasPriceError" cols="12" class="py-0 mt-n3 mb-2">
            <div class="error--text text-caption font-weight-bold d-flex align-center">
              <v-icon small color="error" class="ml-1">mdi-alert-circle-outline</v-icon>
              {{ priceErrorMessage }}
            </div>
          </v-col>

          <!-- توضیحات محصول -->
          <v-col cols="12">
            <v-textarea
              v-model="formData.description"
              label="توضیحات محصول"
              outlined
              dense
              rows="4"
              auto-grow
              prepend-inner-icon="mdi-text"
              :error-messages="serverErrors.description"
            />
          </v-col>

          <!-- ویژگی محصول -->
          <v-col cols="12">
            <v-textarea
              v-model="formData.attributes"
              label="ویژگی محصول"
              outlined
              dense
              rows="3"
              auto-grow
              prepend-inner-icon="mdi-tag-text-outline"
              :error-messages="serverErrors.attributes"
            />
          </v-col>

          <!-- تصاویر محصول اصلی -->
          <v-col cols="12">
            <v-card outlined class="pa-3 rounded-lg bg-white">
              <div class="text-subtitle-2 font-weight-bold primary--text mb-2 d-flex align-center">
                <v-icon small color="primary" class="ml-1">mdi-image-multiple</v-icon>
                گالری تصاویر محصول
              </div>
              <pic-selector
                v-model="productGallery"
                :edit-name="false"
                default-cat-name="محصول"
                :is_digi="true"
                single
              />
            </v-card>
          </v-col>
          <!-- نوع ساختار کالا -->
          <v-col cols="12" :md="formData.has_variants ? 12 : 6">              
            <v-autocomplete
              v-model="formData.has_variants"
              :items="productTypeOptions"
              item-text="text"
              item-value="value"
              :disabled="!!productId"
              label="نوع محصول"
              outlined
              dense
            >
              <template v-slot:selection="{ item }">
                <v-icon right class="ml-2" color="primary">{{ item.icon }}</v-icon>
                <span class="font-weight-bold primary--text">{{ item.text }}</span>
              </template>
              <template v-slot:item="{ item }">
                <v-icon right class="ml-2">{{ item.icon }}</v-icon>
                <span>{{ item.text }}</span>
              </template>
            </v-autocomplete>
          </v-col>

          <!-- موجودی انبار برای کالای ساده -->
          <v-col v-if="!formData.has_variants" cols="12" md="6">
            <v-text-field
              v-model.number="formData.stock"
              label="موجودی انبار"
              type="number"
              outlined
              dense
              prepend-inner-icon="mdi-package-variant"
              :rules="[rules.required, rules.minZero]"
              :error-messages="serverErrors.stock"
            />
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- بخش تنوع‌ها -->
        <div v-if="formData.has_variants" class="mt-4">
          <!-- نوار ابزار بالای تنوع‌ها (شامل عنوان، انتخاب همه، دکمه مدیریت قیمت و افزودن تنوع) -->
          <div class="d-flex flex-wrap justify-space-between align-center mb-3">
            <div class="d-flex align-center">
              <span class="font-weight-bold text-subtitle-2 primary--text ml-3">
                <v-icon small color="primary" class="ml-1">mdi-format-list-bulleted-type</v-icon>
                لیست تنوع‌های محصول (SKUها)
              </span>

              <!-- دکمه انتخاب / لغو انتخاب همه تنوع‌ها -->
              <v-btn
                text
                small
                color="primary"
                class="rounded-lg px-2"
                @click="toggleSelectAllSkus"
              >
                <v-icon left small>
                  {{ isAllSkusSelected ? 'mdi-checkbox-marked' : (isSkusIndeterminate ? 'mdi-minus-box' : 'mdi-checkbox-blank-outline') }}
                </v-icon>
                {{ isAllSkusSelected ? 'لغو انتخاب همه' : 'انتخاب همه' }}
              </v-btn>
            </div>

            <div class="d-flex align-center">
              <!-- دکمه مدیریت قیمت گروهی تنوع‌ها -->
              <v-slide-x-reverse-transition>
                <div v-if="selectedSkus.length > 0" class="d-flex align-center ml-2">
                  <v-chip small class="ml-2 font-weight-bold" color="primary" outlined>
                    {{ selectedSkus.length }} تنوع انتخاب شده
                  </v-chip>
                  <v-btn
                    small
                    class="btn-show rounded-lg px-3"
                    elevation="1"
                    @click="showBulkPriceModal = true"
                  >
                    <v-icon left small>mdi-cash-edit</v-icon>
                    مدیریت قیمت
                  </v-btn>
                </div>
              </v-slide-x-reverse-transition>

              <v-btn small class="btn-get-code rounded-lg" @click="addVariant">
                <v-icon left small>mdi-plus</v-icon>
                افزودن تنوع جدید
              </v-btn>
            </div>
          </div>

          <!-- لیست آکاردئونی تنوع‌ها -->
          <v-expansion-panels v-model="activeVariantPanel" accordion class="mb-4">
            <v-expansion-panel
              v-for="(skuItem, index) in formData.skus"
              :key="index"
              class="elevation-1 rounded-lg mb-2"
            >
              <v-expansion-panel-header class="py-2 px-4 bg-grey-lighten-4">
                <div class="d-flex justify-space-between align-center w-100 pl-3">
                  <div class="d-flex align-center">
                    <!-- چک‌باکس انتخاب تنوع با جلوگیری از بازشدن آکاردئون -->
                    <div @click.stop class="d-inline-flex align-center ml-2">
                      <v-checkbox
                        v-model="selectedSkus"
                        :value="skuItem"
                        hide-details
                        dense
                        class="ma-0 pa-0"
                      />
                    </div>

                    <span class="text-subtitle-2 font-weight-bold">
                      تنوع {{ persian(index + 1) }}: {{ skuItem.title || 'بدون عنوان' }}
                    </span>
                  </div>

                  <v-btn
                    v-if="formData.skus.length > 1"
                    icon
                    color="error"
                    small
                    @click.stop="removeVariant(index)"
                  >
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content class="pt-4">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="skuItem.title"
                      label="عنوان تنوع (مثلاً: رنگ مشکی سایز XL)"
                      outlined
                      dense
                      :rules="[rules.required]"
                      :error-messages="serverErrors[`skus.${index}.title`]"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="skuItem.sku"
                      label="کد SKU تنوع"
                      outlined
                      dense
                      :rules="[rules.required]"
                      :error-messages="serverErrors[`skus.${index}.sku`]"
                    />
                  </v-col>

                  <!-- حداقل و حداکثر قیمت تنوع -->
                  <v-col cols="12" md="6">
                    <price-input
                      v-model="skuItem.min_price"
                      label="حداقل قیمت"
                      dense
                      :class="{ 'has-price-error': hasVariantPriceError(skuItem) }"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <price-input
                      v-model="skuItem.max_price"
                      label="حداکثر قیمت"
                      dense
                      :class="{ 'has-price-error': hasVariantPriceError(skuItem) }"
                    />
                  </v-col>

                  <!-- پیام خطای حداقل/حداکثر قیمت تنوع -->
                  <v-col v-if="hasVariantPriceError(skuItem)" cols="12" class="py-0 mt-n3 mb-2">
                    <div class="error--text text-caption font-weight-bold d-flex align-center">
                      <v-icon small color="error" class="ml-1">mdi-alert-circle-outline</v-icon>
                      حداقل قیمت باید کمتر از حداکثر قیمت باشد.
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model.number="skuItem.stock"
                      label="موجودی"
                      type="number"
                      outlined
                      dense
                      :rules="[rules.required, rules.minZero]"
                      :error-messages="serverErrors[`skus.${index}.stock`]"
                    />
                  </v-col>
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-switch
                      v-model="skuItem.is_active"
                      label="فعال بودن تنوع"
                      color="primary"
                      hide-details
                      dense
                    />
                  </v-col>
                       <!-- تصویر تنوع -->
                  <v-col cols="12">
                    <v-card outlined class="pa-3 rounded-lg bg-white">
                      <div class="text-caption font-weight-bold grey--text text--darken-2 mb-2">
                        <v-icon small class="ml-1">mdi-image</v-icon>
                        تصویر تنوع
                      </div>
                      <pic-selector
                        v-model="skuItem.gallery"
                        :edit-name="false"
                        default-cat-name="تنوع"
                        single
                        :is_digi="true"
                      />
                    </v-card>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>


        <v-row class="mt-2">
          <v-col cols="12">
            <v-switch
              v-model="formData.is_active"
              label="فعال بودن وضعیت کلی کالا"
              color="success"
              inset
              dense
              hide-details
            />
          </v-col>
        </v-row>

        <div class="d-flex flex-row justify-space-between mt-6">
          <v-btn
              class="btn-show rounded-lg"
              elevation="1"
              @click="showPriceLabelModal = true"
            >
              <v-icon left >mdi-qrcode-plus</v-icon>
              ساخت خودکار لیبل قیمت
            </v-btn>
          <v-btn
            :loading="saving"
            class="btn-get-code rounded-lg px-8"
            @click="saveProduct"
            :disabled="!valid || hasPriceError || hasAnyVariantPriceError"
          >
            <v-icon left>mdi-check</v-icon>
            {{ productId ? 'ذخیره تغییرات' : 'ثبت محصول' }}
          </v-btn>

          <v-btn
            :disabled="saving"
            class="btn-delete rounded-lg px-8"
            @click="$emit('close')"
          >
            <v-icon left>mdi-close</v-icon>
            انصراف
          </v-btn>
        </div>
      </div>
    </v-form>
      <generate-price-label-modal
            v-if="showGenerateQrModal"
        v-model="showPriceLabelModal"
        :product="formData"
        :product-gallery="productGallery"
        :selected-skus="selectedSkus"
        @success="selectedSkus = []"
        @close="showGenerateQrModal = false"

      />
     <!-- مدال مدیریت گروهی قیمت تنوع‌ها -->
      <bulk-price-modal
        v-model="showBulkPriceModal"
        :selected-items="selectedSkus"
        @apply="onBulkPriceApply"
      />
  </v-card>
</template>

<script>
import priceInput from "../../../../core/plugins/TitleRibbon/pages/pickers/priceInput.vue";
import { AtomSpinner } from "epic-spinners";
import BulkPriceModal from "./BulkPriceModal.vue";
import PicSelector from "../../../components/UploadCenter/picSelector.vue";
import GeneratePriceLabelModal from "./GeneratePriceLabelModal.vue";

export default {
  name: "productformtab",
  components: { priceInput, AtomSpinner,BulkPriceModal, PicSelector, GeneratePriceLabelModal },
  props: {
    productId: { type: [Number, String], default: null },
  },
  data() {
    return {
      showGenerateQrModal: false,
      showPriceLabelModal:false,
      selectedSkus: [],
      showBulkPriceModal: false,
      productGallery: [{ name: "محصول", images: [] }],

      productTypeOptions: [
        { text: "محصول ساده (تک‌تنوع)", value: false, icon: "mdi-cube-outline" },
        { text: "محصول چندتنوعی", value: true, icon: "mdi-sitemap" },
      ],
      valid: false,
      loading: false,
      saving: false,
      activeVariantPanel: 0,
      serverErrors: {},
      formData: this.getInitialFormData(),
      rules: {
        required: (v) =>
          (v !== null && v !== "" && v !== undefined) || "این فیلد الزامی است.",
        minZero: (v) => v >= 0 || "مقدار نمی‌تواند منفی باشد.",
      },
    };
  },

  computed: {
        isAllSkusSelected() {
      return (
        this.formData.skus &&
        this.formData.skus.length > 0 &&
        this.selectedSkus.length === this.formData.skus.length
      );
    },
    isSkusIndeterminate() {
      return (
        this.selectedSkus.length > 0 &&
        this.selectedSkus.length < this.formData.skus.length
      );
    },
    hasAnyVariantPriceError() {
      if (!this.formData.has_variants || !this.formData.skus) return false;
      return this.formData.skus.some((sku) => this.hasVariantPriceError(sku));
    },
    cleanMinPrice() {
      return this.parseNumericPrice(this.formData.default_min_price);
    },
    cleanMaxPrice() {
      return this.parseNumericPrice(this.formData.default_max_price);
    },
    hasPriceError() {
      if (!this.cleanMinPrice || !this.cleanMaxPrice) return false;
      return this.cleanMinPrice >= this.cleanMaxPrice;
    },
    priceErrorMessage() {
      return this.hasPriceError ? "حداقل قیمت باید کمتر از حداکثر قیمت باشد." : "";
    },
  },

  watch: {
    productId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchProductDetail();
        } else {
          this.resetForm();
        }
      },
    },

    'formData.has_variants'(newVal) {
      if (newVal) {
        this.formData.skus.forEach((skuItem, index) => {
          if (!skuItem.sku ) {
            skuItem.sku = `${this.formData.sku}-${index + 1}`;
          }
          if (!skuItem.min_price || skuItem.min_price === 0) {
            skuItem.min_price = this.formData.default_min_price;
          }
          if (!skuItem.max_price || skuItem.max_price === 0) {
            skuItem.max_price = this.formData.default_max_price;
          }
        });
      }
    },

'formData.sku'(newVal, oldVal) {
    if (!newVal || !this.formData.skus) return;

    this.formData.skus.forEach((skuItem, index) => {
      const expectedOldPattern = oldVal ? `${oldVal}-${index + 1}` : null;

      if (!skuItem.sku || skuItem.sku === expectedOldPattern) {
        skuItem.sku = `${newVal}-${index + 1}`;
      }
    });
  },
    'formData.default_min_price'(newVal) {
      this.formData.skus.forEach(sku => {
        if (!sku.min_price || sku.min_price === 0) {
          sku.min_price = newVal;
        }
      });
    },

    'formData.default_max_price'(newVal) {
      this.formData.skus.forEach(sku => {
        if (!sku.max_price || sku.max_price === 0) {
          sku.max_price = newVal;
        }
      });
    },
  },

  methods: {
    onBulkPriceApply(config) {
  if (!this.selectedSkus || !this.selectedSkus.length) return;

  this.selectedSkus.forEach((sku) => {
    if (config.operation === "set_range") {
      // تغییر مستقیم حداقل و حداکثر قیمت به مقادیر وارد شده در مدال
      sku.min_price = config.min_price;
      sku.max_price = config.max_price;
    } else {
      // افزایش یا کاهش: اعمال روی هر دو پارامتر min_price و max_price
      ["min_price", "max_price"].forEach((priceKey) => {
        let currentPrice = this.parseNumericPrice(sku[priceKey]);
        let change = 0;

        if (config.type === "percent") {
          change = Math.round((currentPrice * config.value) / 100);
        } else {
          change = config.value;
        }

        if (config.operation === "increase") {
          sku[priceKey] = currentPrice + change;
        } else if (config.operation === "decrease") {
          // جلوگیری از منفی شدن قیمت
          sku[priceKey] = Math.max(0, currentPrice - change);
        }
      });
    }
  });

  this.$toast.success("قیمت تنوع‌های انتخاب شده با موفقیت به‌روزرسانی شد");
  this.selectedSkus = []; 
},

    extractUrl(img) {
      if (!img) return "";
      if (typeof img === "string") return img;
      return img.url || img.path || img.src || "";
    },
    formatImagesForPayload(galleryArray) {
      if (!galleryArray || !Array.isArray(galleryArray) || galleryArray.length === 0) {
        return [];
      }

      let allImages = [];
      galleryArray.forEach(cat => {
        if (cat && Array.isArray(cat.images)) {
          cat.images.forEach(img => {
            if (img) allImages.push(img);
          });
        }
      });

      if (allImages.length === 0) return [];

      const hasMainImage = allImages.some(img => {
        if (typeof img === "object") {
          return img.is_main === true || img.is_main === 1 || img.is_main === "1";
        }
        return false;
      });

      return allImages.map((img, index) => {
        const url = this.extractUrl(img);
        const alt = typeof img === "object" ? img.alt || "" : "";
        
        const isExplicitlyMain = typeof img === "object" && (img.is_main === true || img.is_main === 1 || img.is_main === "1");
        
        const isMain = hasMainImage ? isExplicitlyMain : (index === 0);

        return {
          url: url,
          alt: alt,
          is_main: isMain,
          sort_order: typeof img === "object" && img.sort_order !== undefined ? img.sort_order : index
        };
      }).filter(item => !!item.url);
    },  
    formatImagesForGallery(imagesArray, defaultCat = "تصاویر") {
      if (!imagesArray || !Array.isArray(imagesArray)) {
        return [{ name: defaultCat, images: [] }];
      }

      const list = imagesArray.map((img, idx) => {
        if (typeof img === "string") {
          return {
            url: img,
            alt: "",
            is_main: idx === 0,
            sort_order: idx,
          };
        }
        return {
          ...img,
          url: this.extractUrl(img),
          alt: img.alt || "",
          is_main: Boolean(img.is_main),
          sort_order: img.sort_order !== undefined ? img.sort_order : idx,
        };
      }).filter((item) => !!item.url);

      return [{ name: defaultCat, images: list }];
    },

    parseNumericPrice(val) {
      if (!val) return 0;
      const normalized = String(val)
        .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
        .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d))
        .replace(/,/g, "")
        .trim();
      return Number(normalized) || 0;
    },

    hasVariantPriceError(sku) {
      if (!sku) return false;
      const min = this.parseNumericPrice(sku.min_price);
      const max = this.parseNumericPrice(sku.max_price);
      if (min > 0 && max > 0) {
        return min >= max;
      }
      return false;
    },

    getInitialFormData() {
      return {
        title: "",
        sku: "",
        description: "",
        has_variants: false,
        stock: 0,
        default_min_price: 0,
        default_max_price: 0,
        is_active: true,
        skus: [this.getEmptySku()],
        weight: 0,
        length: 0,
        width: 0,
        height: 0,
        attributes: "",

      };
    },

    resetForm() {
      this.formData = this.getInitialFormData();
      this.productGallery = [{ name: "محصول", images: [] }];

      this.serverErrors = {};
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    getEmptySku(suggestedSku = "") {
      return {
        id: null,
        title: "",
        sku: suggestedSku,
        stock: 1000, 
        min_price: this.cleanMinPrice || 0,
        max_price: this.cleanMaxPrice || 0,
        is_active: true,
        gallery: [{ name: "تنوع", images: [] }],

      };
    },

    addVariant() {
      const parentSku = this.formData.sku || "SKU";
      const suggestedSku = `${parentSku}-${this.formData.skus.length + 1}`;
      this.formData.skus.push(this.getEmptySku(suggestedSku));
      this.$nextTick(() => {
        this.activeVariantPanel = this.formData.skus.length - 1;
      });
    },

     toggleSelectAllSkus() {
      if (this.isAllSkusSelected) {
        this.selectedSkus = [];
      } else {
        this.selectedSkus = [...this.formData.skus];
      }
    },

    async onBulkPriceSuccess() {
      this.selectedSkus = [];
      if (this.productId) {
        await this.fetchProductDetail();
      }
    },

    removeVariant(index) {
      const removed = this.formData.skus[index];
      this.formData.skus.splice(index, 1);
      this.selectedSkus = this.selectedSkus.filter((sku) => sku !== removed);
      if (this.activeVariantPanel >= this.formData.skus.length) {
        this.activeVariantPanel = Math.max(0, this.formData.skus.length - 1);
      }
    },


    async fetchProductDetail() {
      this.loading = true;
      try {
        const res = await this.$api.get(`/v1/products/show/${this.productId}`);
        const data = res.data?.data || res.data;
        this.productGallery = this.formatImagesForGallery(data.images, "محصول");
        this.formData = {
          title: data.title || "",
          sku: data.sku || "",
          description: data.description || "",
          has_variants: Boolean(data.has_variants),
          stock: data.stock || 0,
          default_min_price: data.default_min_price || 0,
          default_max_price: data.default_max_price || 0,
          is_active: data.is_active !== undefined ? Boolean(data.is_active) : true,
          skus: data.skus && data.skus.length > 0 
            ? data.skus.map((item) => ({
                ...item,
                min_price: item.min_price || 0,
                max_price: item.max_price || 0,
                  gallery: this.formatImagesForGallery(item.images, "تنوع"),
              })) 
            : [this.getEmptySku()],
          weight: data.weight || 0,
          length: data.length || 0,
          width: data.width || 0,
          height: data.height || 0,
          attributes: data.attributes || "",
        };
      } catch (e) {
        this.$toast.error("خطا در بارگذاری اطلاعات محصول");
      } finally {
        this.loading = false;
      }
    },

       async saveProduct() {
      this.serverErrors = {};
      if (!this.$refs.form.validate() || this.hasPriceError || this.hasAnyVariantPriceError) {
        this.$toast.error(
          this.hasPriceError || this.hasAnyVariantPriceError
            ? "حداقل قیمت باید کمتر از حداکثر قیمت باشد."
            : "لطفاً خطاهای فرم را برطرف کنید."
        );
        return;
      }

      if (
        this.formData.has_variants &&
        (!this.formData.skus || this.formData.skus.length === 0)
      ) {
        this.$toast.error("برای محصول متغیر، حداقل یک تنوع باید تعریف شود.");
        return;
      }

      this.saving = true;

      const payload = {
        title: this.formData.title,
        sku: this.formData.sku,
        description: this.formData.description,
        has_variants: this.formData.has_variants,
        is_active: this.formData.is_active,
        default_min_price: this.cleanMinPrice || null,
        default_max_price: this.cleanMaxPrice || null,
        weight: Number(this.formData.weight || 0),
        length: Number(this.formData.length || 0),
        width: Number(this.formData.width || 0),
        height: Number(this.formData.height || 0),
        attributes: this.formData.attributes,
        images: this.formatImagesForPayload(this.productGallery),

      };

      if (!this.formData.has_variants) {
        payload.stock = Number(this.formData.stock);
      } else {
        payload.skus = this.formData.skus.map((sku) => {
          const skuData = {
            title: sku.title,
            sku: sku.sku,
            stock: Number(sku.stock),
            min_price: this.parseNumericPrice(sku.min_price),
            max_price: this.parseNumericPrice(sku.max_price),
            is_active: Boolean(sku.is_active),
            images: this.formatImagesForPayload(sku.gallery),

          };
          if (sku.id) skuData.id = sku.id;
          return skuData;
        });
      }

      const url = this.productId
        ? `/v1/products/${this.productId}`
        : `/v1/products/store`;

      try {
        const res = await this.$api.post(url, payload);
        const resData = res.data;
        console.log(resData,"res");
        const newId =
          this.productId ||
          resData?.data?.id ||
          resData?.id ||
          resData?.data?.product?.id ||
          resData?.product?.id;

        this.$toast.success(
          this.productId ? "محصول با موفقیت ویرایش شد" : "محصول با موفقیت ثبت شد"
        );
        
        this.$emit("saved", {
          id: newId,
          title: this.formData.title,
        });
      } catch (error) {
        if (error.response?.status === 422 && error.response?.data?.errors) {
          this.serverErrors = error.response.data.errors;
          this.$toast.error("خطای اعتبارسنجی فیلدها را بررسی نمایید.");
        } else {
          this.$toast.error(
            error.response?.data?.message || "خطا در ذخیره‌سازی اطلاعات"
          );
        }
      } finally {
        this.saving = false;
      }
    },

  },
};
</script>

<style scoped>
::v-deep .has-price-error fieldset {
  border-color: #ff5252 !important;
  border-width: 2px !important;
}
</style>
